import React, { useState, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/cjs/styles/prism";
import { InterviewSettings } from "../types";
import "./InterviewEditorChatPanel.css";

export interface InterviewEditorChatPanelProps {
    darkMode: boolean;
    userId: string | null;
    interview: InterviewSettings;
    onSettingsUpdated: (updates: Partial<InterviewSettings>) => void;
}

interface ChatMessage {
    role: "user" | "assistant";
    content: string;
    toolCall?: string;
}

const getApiBase = () => {
    if (process.env.NODE_ENV === "production") {
        return process.env.REACT_APP_BACKEND_URL || "";
    }
    return process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
};

const InterviewEditorChatPanel: React.FC<InterviewEditorChatPanelProps> = ({
    darkMode,
    userId,
    interview,
    onSettingsUpdated,
}) => {
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [input, setInput] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [currentToolCall, setCurrentToolCall] = useState<string | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const prevLoadingRef = useRef(false);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, currentToolCall]);

    useEffect(() => {
        if (prevLoadingRef.current && !isLoading) {
            inputRef.current?.focus();
        }
        prevLoadingRef.current = isLoading;
    }, [isLoading]);

    const handleSend = async () => {
        const text = input.trim();
        if (!text || !userId || isLoading) return;

        const userMessage: ChatMessage = { role: "user", content: text };
        setMessages((prev) => [...prev, userMessage]);
        setInput("");
        setIsLoading(true);
        setTimeout(() => inputRef.current?.focus(), 0);

        try {
            const currentInterview = {
                title: interview.title,
                description: interview.description,
                content: interview.content,
                language: interview.language,
                difficulty: interview.difficulty,
                status: interview.status,
                instructions: interview.instructions,
                timeLimit: interview.timeLimit,
                allowedLanguages: interview.allowedLanguages,
                agentSystemPrompt: interview.agentSystemPrompt,
            };

            const response = await fetch(`${getApiBase()}/api/ai/interview-editor-agent`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    messages: [...messages, userMessage].map((m) => ({
                        role: m.role,
                        content: m.content,
                    })),
                    userId,
                    currentInterview,
                    stream: true,
                }),
            });

            if (!response.ok) {
                throw new Error("Failed to get response");
            }

            const reader = response.body?.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            let streamingContent = "";
            let toolsUsed: string[] = [];
            let finalUpdates: Partial<InterviewSettings> | null = null;
            let finalResponse = "";

            if (reader) {
                setMessages((prev) => [...prev, { role: "assistant", content: "" }]);

                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;

                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split("\n");
                    buffer = lines.pop() || "";

                    for (const line of lines) {
                        if (line.startsWith("data: ")) {
                            const data = line.slice(6);
                            if (data === "[DONE]") continue;

                            try {
                                const parsed = JSON.parse(data);
                                if (parsed.type === "text" && parsed.content) {
                                    streamingContent += parsed.content;
                                    const contentToSet = streamingContent;
                                    setMessages((prev) => {
                                        const next = [...prev];
                                        const last = next[next.length - 1];
                                        if (last?.role === "assistant") {
                                            next[next.length - 1] = { ...last, content: contentToSet };
                                        }
                                        return next;
                                    });
                                } else if (parsed.type === "tool_call" && parsed.name) {
                                    if (!toolsUsed.includes(parsed.name)) toolsUsed.push(parsed.name);
                                    setCurrentToolCall(parsed.name);
                                } else if (parsed.type === "complete") {
                                    finalResponse = parsed.response || "";
                                    if (parsed.updates && Object.keys(parsed.updates).length > 0) {
                                        finalUpdates = parsed.updates;
                                        onSettingsUpdated(parsed.updates);
                                    }
                                    let content = finalResponse;
                                    if (content && toolsUsed.length > 0) {
                                        content = `Updated: ${toolsUsed.map((t) => t.replace(/_/g, " ")).join(", ")}\n\n${content}`;
                                    }
                                    if (content) {
                                        streamingContent = content;
                                        setMessages((prev) => {
                                            const next = [...prev];
                                            const last = next[next.length - 1];
                                            if (last?.role === "assistant") {
                                                next[next.length - 1] = { ...last, content };
                                            } else {
                                                next.push({ role: "assistant", content });
                                            }
                                            return next;
                                        });
                                    }
                                }
                            } catch {
                                // ignore parse errors
                            }
                        }
                    }
                }

                const assistantContent = streamingContent || finalResponse;
                if (assistantContent && finalUpdates) {
                    // Updates already applied via onSettingsUpdated
                }
            } else {
                // Fallback: non-streaming response
                const data = await response.json();
                if (data.toolCall) setCurrentToolCall(data.toolCall);

                if (data.updates && Object.keys(data.updates).length > 0) {
                    onSettingsUpdated(data.updates);
                }

                const assistantContent = data.response || "I've updated the settings.";
                if (assistantContent) {
                    setMessages((prev) => [...prev, { role: "assistant", content: assistantContent }]);
                }
            }
        } catch (err) {
            setMessages((prev) => {
                const last = prev[prev.length - 1];
                if (last?.role === "assistant" && last.content === "") {
                    return [...prev.slice(0, -1), { ...last, content: "Sorry, something went wrong. Please try again." }];
                }
                return [...prev, { role: "assistant" as const, content: "Sorry, something went wrong. Please try again." }];
            });
        } finally {
            setIsLoading(false);
            setCurrentToolCall(null);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    return (
        <div className={`interview-editor-chat-panel ${darkMode ? "dashboard-dark" : ""}`}>
            <div className="interview-editor-chat-header">
                <h3>Settings Assistant</h3>
                <p>
                    Ask me to adjust interview settings. I can change difficulty, time limit,
                    instructions, and more.
                </p>
            </div>

            <div className="interview-editor-chat-messages">
                {messages.length === 0 && !currentToolCall && (
                    <div className="interview-editor-chat-empty">
                        <p>Tell me what to change.</p>
                        <p>e.g. &quot;Set difficulty to Hard&quot;, &quot;60 minute time limit&quot;</p>
                    </div>
                )}
                {messages.map((msg, i) => (
                    <div
                        key={i}
                        className={`interview-editor-chat-message interview-editor-chat-markdown ${
                            msg.role === "user" ? "user" : "assistant"
                        }`}
                    >
                        <ReactMarkdown
                            components={{
                                code({ node, className, children, ...props }) {
                                    const match = /language-(\w+)/.exec(className || "");
                                    const content = String(children).replace(/\n$/, "");
                                    const isInline = !match && !content.includes("\n");

                                    if (isInline) {
                                        return (
                                            <code className={className} {...props}>
                                                {children}
                                            </code>
                                        );
                                    }
                                    return (
                                        <SyntaxHighlighter
                                            style={oneDark as Record<string, React.CSSProperties>}
                                            language={match ? match[1] : "text"}
                                            PreTag="div"
                                            customStyle={{
                                                margin: "0.5em 0",
                                                borderRadius: "6px",
                                                fontSize: "0.85em",
                                                padding: "0.8em",
                                            }}
                                        >
                                            {content}
                                        </SyntaxHighlighter>
                                    );
                                },
                            }}
                        >
                            {msg.content}
                        </ReactMarkdown>
                    </div>
                ))}
                {currentToolCall && (
                    <div className="interview-editor-chat-message tool-call">
                        Updating: {currentToolCall.replace(/_/g, " ")}
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            <div className="interview-editor-chat-input-wrap">
                <div className="interview-editor-chat-input-row">
                    <textarea
                        ref={inputRef}
                        className="interview-editor-chat-input"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="e.g. Make it harder, set 90 min limit..."
                        rows={1}
                        disabled={isLoading}
                    />
                    <button
                        className="interview-editor-chat-send"
                        onClick={handleSend}
                        disabled={isLoading || !input.trim()}
                    >
                        Send
                    </button>
                </div>
            </div>
        </div>
    );
};

export default InterviewEditorChatPanel;
